import { useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Menu, ChevronRight } from 'lucide-react';
import type { DockviewPanelApi } from 'dockview-react';
import { useAppStore, usePanel, type LinkGroupColor, type PanelId, type TabId } from './store.js';
import { useDockviewApi } from './dockview-context.js';
import { isChannellessPanel } from './panel-registry.js';

// SaxoTrader-style panel header menu. Hamburger icon → small popover
// anchored under the trigger (NOT a centered modal). Behaves like a
// right-click context menu — close on outside-click, Escape, or item
// pick. Built on Radix DropdownMenu so the keyboard nav + focus
// trapping is correct out of the box.
//
//   Link        →  submenu of channels (coloured swatches) + Off
//   Duplicate   →  addPanel with same component/params at a new id
//   Undock      →  promote the panel into a floating mirror
//   Delete      →  removePanel + api.close()
//
// Channel-less panels (landing aggregates) skip the Link row.

const CHANNEL_COLOURS: Record<LinkGroupColor, string> = {
  default: 'var(--neutral-data)',
  claret: 'var(--claret)',
  blue: 'var(--link)',
  teal: 'var(--teal)',
  terracotta: 'var(--accent)',
};

// The channels were renamed (red→claret, green→teal, yellow→terracotta). Some
// persisted workspaces still carry the retired colour values; normalise them at
// render so the swatch + label always resolve (a migration can't reliably reach
// every tab once a dockview layout has been restored over it).
const COLOUR_ALIAS: Record<string, LinkGroupColor> = {
  red: 'claret',
  green: 'teal',
  yellow: 'terracotta',
};
const normColour = (c: string): LinkGroupColor => COLOUR_ALIAS[c] ?? (c as LinkGroupColor);

export function PanelHeaderMenu({
  tabId,
  panelId,
  dockviewPanelApi,
}: {
  tabId: TabId;
  panelId: PanelId;
  /** The panel's own dockview API, threaded from CustomTab. Used as a
   *  fallback when the store has lost track of this panel (e.g. an
   *  orphaned panel whose store entry was removed independently) so
   *  Delete / Undock still work via the panel's own handle. */
  dockviewPanelApi?: DockviewPanelApi;
}) {
  const { t } = useTranslation();
  const panel = usePanel(tabId, panelId);
  const groups = useAppStore((s) => s.tabs[tabId]?.groups);
  const movePanelToGroup = useAppStore((s) => s.actions.movePanelToGroup);
  const pinPanel = useAppStore((s) => s.actions.pinPanel);
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const apiRef = useDockviewApi();
  const isFloating = useIsPanelFloating(panelId);

  // dockview is the authoritative source for "what kind of panel am I?"
  // — its api.component is set at addPanel time and survives store-state
  // gaps (e.g. a brand-new floating panel whose store entry was just
  // written but where this menu's render captured a pre-update snapshot).
  // Fall back to the store's record when the dockview ref isn't around.
  const dvComponent = (dockviewPanelApi?.component as string | undefined) ?? panel?.component;
  const dvTitle = dockviewPanelApi?.title ?? panel?.title ?? '';
  const channelless = dvComponent ? isChannellessPanel(dvComponent) : false;

  const currentGroupId = panel && panel.link.kind === 'linked' ? panel.link.groupId : null;
  const isPinned = panel?.link.kind === 'pinned';
  // Hamburger icon doubles as the channel indicator — colour matches
  // whichever channel the panel is linked to. Pinned panels (no
  // channel) fall back to a neutral grey.
  const triggerColour = isPinned
    ? 'var(--text-tertiary)'
    : currentGroupId && groups?.[currentGroupId]
      ? CHANNEL_COLOURS[normColour(groups[currentGroupId].color)]
      : CHANNEL_COLOURS.default;

  const channels = useMemo(() => {
    if (!groups) return [];
    return Object.values(groups)
      .filter((g) => g.id !== 'default')
      .map((g) => ({
        id: g.id,
        label: t(`channelPicker.colors.${normColour(g.color)}` as const),
        color: normColour(g.color),
      }));
  }, [groups, t]);

  function linkToChannel(groupId: string) {
    movePanelToGroup(tabId, panelId, groupId);
  }
  function turnOff() {
    pinPanel(tabId, panelId);
  }

  function duplicate() {
    if (!apiRef.current || !dvComponent) return;
    const api = apiRef.current;
    const original = api.getPanel(panelId);
    if (!original) return;
    const newId = addPanelToStore(tabId, {
      component: dvComponent,
      title: dvTitle,
    });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel({
      id: newId,
      component: dvComponent,
      title: dvTitle,
      params: { tabId, panelId: newId },
      floating: {
        position: { left: 120 + offset, top: 80 + offset },
        width: 520,
        height: 400,
      },
    });
  }

  function undock() {
    if (!apiRef.current) return;
    const api = apiRef.current;
    const original = api.getPanel(panelId);
    if (!original) return;
    // Promote the existing panel into a floating group — keeps the
    // same panel id, store entry, and panel state intact. The previous
    // implementation cloned the panel into a new floating one and left
    // the docked original behind, so the same panel appeared twice.
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addFloatingGroup(original, {
      position: { left: 120 + offset, top: 80 + offset },
      width: 520,
      height: 400,
    });
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('panelHeaderMenu.openAria')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            padding: 2,
            color: triggerColour,
            cursor: 'pointer',
            lineHeight: 0,
          }}
        >
          <Menu size={14} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          style={menuContent()}
        >
          {!channelless && panel && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger style={menuRow()}>
                <span>{t('panelHeaderMenu.link')}</span>
                <ChevronRight size={12} color="var(--text-tertiary)" />
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent sideOffset={4} alignOffset={-4} style={menuContent()}>
                  {channels.map((c) => (
                    <DropdownMenu.CheckboxItem
                      key={c.id}
                      checked={!isPinned && currentGroupId === c.id}
                      onSelect={() => linkToChannel(c.id)}
                      style={menuRow()}
                    >
                      <span>{c.label}</span>
                      <Swatch color={CHANNEL_COLOURS[c.color]} />
                    </DropdownMenu.CheckboxItem>
                  ))}
                  <DropdownMenu.CheckboxItem
                    checked={!isPinned && currentGroupId === 'default'}
                    onSelect={() => linkToChannel('default')}
                    style={menuRow()}
                  >
                    <span>{t('channelPicker.colors.default')}</span>
                    <Swatch color={CHANNEL_COLOURS.default} />
                  </DropdownMenu.CheckboxItem>
                  <DropdownMenu.CheckboxItem
                    checked={!!isPinned}
                    onSelect={turnOff}
                    style={menuRow()}
                  >
                    <span>{t('panelHeaderMenu.off')}</span>
                    <Swatch color="var(--surface-elevated)" />
                  </DropdownMenu.CheckboxItem>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          )}
          {dvComponent && (
            <DropdownMenu.Item onSelect={duplicate} style={menuRow()}>
              {t('panelHeaderMenu.duplicate')}
            </DropdownMenu.Item>
          )}
          {dvComponent && !isFloating && (
            <DropdownMenu.Item onSelect={undock} style={menuRow()}>
              {t('panelHeaderMenu.undock')}
            </DropdownMenu.Item>
          )}
          {/* Close moved to a direct per-tab ✕ (see custom-tab.tsx). */}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// Subscribe to a panel's dock-vs-float location so the menu can hide
// the Undock row when the panel is already floating. dockview emits
// onDidLocationChange whenever a panel transitions between dock,
// float, and popout — read it via useSyncExternalStore so React stays
// in sync with the dockview-owned state.
function useIsPanelFloating(panelId: PanelId): boolean {
  const apiRef = useDockviewApi();
  const subscribe = (notify: () => void) => {
    const api = apiRef.current;
    if (!api) return () => {};
    const panel = api.getPanel(panelId);
    if (!panel) return () => {};
    const sub = panel.api.onDidLocationChange(notify);
    return () => sub.dispose();
  };
  const getSnapshot = () => apiRef.current?.getPanel(panelId)?.api.location.type ?? 'docked';
  const loc = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return loc === 'floating';
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: 2,
        border: '1px solid var(--hairline)',
        background: color,
      }}
    />
  );
}

function menuContent(): React.CSSProperties {
  return {
    minWidth: 180,
    background: 'var(--menu-surface)',
    border: '1px solid var(--hairline)',
    borderRadius: 4,
    boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
    padding: 4,
    fontSize: 'var(--t-body)',
    // Must sit above any stacked floating dockview panel — see tokens.css
    // --z-overlay note. Dockview pushes 2nd+ floating panels to z-index
    // 1002+, occluding any portal that doesn't reserve headroom. The
    // literal 9000 here mirrors --z-overlay; keep them in sync.
    zIndex: 9000,
  };
}

function menuRow(): React.CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 'var(--s-3)',
    padding: '6px 10px',
    cursor: 'pointer',
    outline: 'none',
    borderRadius: 2,
    color: 'var(--text-primary)',
  };
}
